import { useId, type ReactNode } from "react";
import { cx } from "./cx";
import { IconError } from "./icons";
import "./Field.css";

export type FieldSize = "sm" | "md" | "lg";

export interface FieldIds {
  controlId: string;
  hintId: string | undefined;
  errorId: string | undefined;
  /** Value for the control's aria-describedby (error first, then hint). */
  describedBy: string | undefined;
}

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  /** Error message. Truthy → the control is invalid (aria-invalid) and the message is announced. */
  error?: ReactNode;
  /** Explicit id for the control; generated otherwise. */
  id?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  /** Render prop that receives the ids to wire onto the control. */
  children: (ids: FieldIds) => ReactNode;
}

/** Label + control + hint/error layout shared by Input, Textarea and Select. */
export function Field({
  label,
  hint,
  error,
  id,
  required,
  disabled,
  className,
  children,
}: FieldProps) {
  const auto = useId();
  const controlId = id ?? `f${auto}`;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(" ") || undefined;

  return (
    <div
      className={cx("ui-field", disabled && "ui-field--disabled", error ? "ui-field--invalid" : false, className)}
    >
      {label ? (
        <label className="ui-field__label" htmlFor={controlId}>
          {label}
          {required ? (
            <span className="ui-field__required" aria-hidden="true">
              *
            </span>
          ) : null}
        </label>
      ) : null}
      {children({ controlId, hintId, errorId, describedBy })}
      {error ? (
        <p className="ui-field__error" id={errorId}>
          <IconError />
          <span>{error}</span>
        </p>
      ) : null}
      {hint ? (
        <p className="ui-field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}
